import { createDefaultManager } from "./defaultManager";
import { onMounted, ref } from "vue";
import { Cluster } from "./clusterModel";
import { clamp } from "lodash";
import { createGridManager } from "./gridManager";
import { useGlobalSchoolDataStore } from "store/modules/globalSchoolDataStore";
import { useLearningObjectivesStore } from "store/modules/learningObjectivesStore";
import { useUserStore } from "store/modules/userStore";
import { storeToRefs } from "pinia";

const createClusterManager = () => {
  const { weeks, deltaState, fetchAndSetWeeks, startDelta, endDelta } =
    createDefaultManager();

  const {
    grid,
    hasGridGroups,
    isRendered,
    initializeGrid,
    removeWeekFromRow,
    addWeekToRow,
    removeItemFromGrid,
    generateGridGroup,
    toggleGroupVisibility,
    findGridGroupIdByIndex,
    addGridItem,
  } = createGridManager();

  // Cluster Refs
  const clusters = ref([]);
  const lessonGroups = ref([]);
  const lessonGroupClusters = ref([]);
  const isFetchingClusters = ref(false);
  const isInitializing = ref(false);
  const isInitialized = ref(false);

  // Initialize the cluster manager
  const init = async ({ force = false } = {}) => {
    if (isInitializing.value) {
      return isInitializing.value;
    }

    if (isInitialized.value && !force) {
      return;
    }

    isInitializing.value = (async () => {
      // Fetch weeks, lesson groups
      await Promise.all([fetchAndSetWeeks(), fetchAndSetLessonGroups()]);

      // Fetch and set clusters
      await fetchClusters();

      // Initialize the grid
      initializeGrid(clusters.value, weeks.value, lessonGroupClusters.value);

      isInitializing.value = false;
    })();

    isInitialized.value = true;
  };

  const fetchAndSetLessonGroups = async () => {
    const globalSchoolDataStore = useGlobalSchoolDataStore();
    const { fetchLearningObjectivesLessongroups } = globalSchoolDataStore;

    let fetchedLessonGroups = [];

    try {
      fetchedLessonGroups = await fetchLearningObjectivesLessongroups();
    } catch (error) {
      console.log("🚀 ~ fetchAndSetLessonGroups ~ error:", error);
    }

    lessonGroups.value = fetchedLessonGroups;

    return lessonGroups.value;
  };

  const generateClusterGroups = async (lessonGroups) => {
    lessonGroupClusters.value = lessonGroups.map(
      ({ name: id, lesson_class: name, subject }) => {
        const meta = { subject };

        return generateGridGroup({ id, name, meta });
      },
    );
  };

  const setDraggingStateOnClusterGroup = (groupId, value) => {
    findClusterGroupById(groupId).isDragging = value;
  };

  const findClusterGroupById = (id = null) => {
    if (!id) {
      throw new Error("Id is required to find a cluster group");
    }

    const foundClusterGroup = lessonGroupClusters.value.find(
      ({ id: groupId }) => groupId === id,
    );

    if (foundClusterGroup === undefined) {
      throw new Error(`Cluster group with id: ${id} not found`);
    }

    return foundClusterGroup;
  };

  const fetchClusters = async ({ force = false } = {}) => {
    if (isFetchingClusters.value && !force) {
      return isFetchingClusters.value;
    }

    isFetchingClusters.value = (async () => {
      const learningObjectivesStore = useLearningObjectivesStore();
      const { fetchClusters } = learningObjectivesStore;

      try {
        const fetchedClusters = await fetchClusters({
          forceFetch: force,
        });

        // Uncomment the code below to add lesson groups to the grid based on the fetched clusters
        fetchedClusters.forEach(({ lesson_class, subject_shortage }) => {
          if (
            lessonGroups.value.find(
              ({ lesson_class: _lessonClass, subject }) =>
                _lessonClass === lesson_class && subject === subject_shortage,
            )
          ) {
            return;
          }

          lessonGroups.value.push({
            lesson_class,
            name: lesson_class,
            subject: subject_shortage,
          });
        });

        // This will create groups in the grid based on the lesson groups
        generateClusterGroups(lessonGroups.value);

        setFetchedClusters(fetchedClusters);

        return fetchedClusters;
      } catch (error) {
        console.log("🚀 ~ isFetching.value['clusters']= ~ error:", error);
      } finally {
        isFetchingClusters.value = false;
      }
    })();

    return isFetchingClusters.value;
  };

  const setFetchedClusters = (data) => {
    clusters.value = [];

    if (!Array.isArray(data)) {
      throw new Error("Clusters must be an array");
    }

    data.forEach((cluster) =>
      clusters.value.push(new Cluster({ ...cluster, temporary: false })),
    );

    return clusters.value;
  };

  const addCluster = (data) => {
    const userStore = useUserStore();
    const { user } = storeToRefs(userStore);

    // We add the current user to the cluster, only for temporary clusters
    // Once saved the data will be updated with the correct user
    // and passed back from the backend
    data.user = user.value;

    const newCluster = new Cluster(data);

    clusters.value.push(newCluster);

    return newCluster;
  };

  const updateDelta = (cluster, changeInColumns, handleSide, deltaX) => {
    if (!cluster || !deltaState[cluster.id] || !changeInColumns) {
      return;
    }

    const {
      id: clusterId,
      planningMeta: { weekIndexes },
    } = cluster;
    const {
      startWeekIndex: currentStartWeekIndex,
      endWeekIndex: currentEndWeekIndex,
    } = deltaState[clusterId];

    // Update previous deltaStart with the current deltaX
    deltaState[clusterId].deltaStart = deltaX;

    if (handleSide === "end") {
      const newEndIndex = clamp(
        currentEndWeekIndex + changeInColumns,
        weekIndexes[0],
        weeks.value.length - 1,
      );

      // Maybe check a different way to pass the removeWeekFromRow and addWeekToRow
      cluster.updateEndWeekByIndex(
        newEndIndex,
        currentEndWeekIndex,
        removeWeekFromRow,
        addWeekToRow,
      );

      deltaState[clusterId].endWeekIndex = newEndIndex;
    }

    if (handleSide === "start") {
      const newStartIndex = clamp(
        currentStartWeekIndex + changeInColumns,
        0,
        weekIndexes[weekIndexes.length - 1],
      );

      // Maybe check a different way to pass the removeWeekFromRow and addWeekToRow
      cluster.updateStartWeekByIndex(
        newStartIndex,
        currentStartWeekIndex,
        removeWeekFromRow,
        addWeekToRow,
      );

      deltaState[clusterId].startWeekIndex = newStartIndex;
    }
  };

  const updateClusterPlanning = async () => {
    return await Promise.all(clusters.value.map((cluster) => cluster.upsert()));
  };

  const findClusterById = (id) => {
    const foundCluster = clusters.value.find(
      (cluster) => cluster.id === parseInt(id),
    );

    if (foundCluster === undefined) {
      throw new Error(`Cluster with id: ${id} not found`);
    }

    return foundCluster;
  };

  const removeCluster = async (clusterId) => {
    const cluster = findClusterById(clusterId);

    // Delete cluster from the server
    try {
      await cluster.delete();

      const clusterIndex = clusters.value.findIndex(
        ({ id }) => id === clusterId,
      );

      if (clusterIndex === -1) {
        throw new Error(`Cluster with id: ${clusterId} not found`);
      }

      // Remove from the local store
      clusters.value.splice(clusterIndex, 1);

      // Remove from the grid
      const { rowIndex, weekIndexes } = cluster.planningMeta;
      removeItemFromGrid(rowIndex, weekIndexes, cluster);
    } catch (error) {
      console.log("🚀 ~ removeCluster ~ error:", error);
    }
  };

  const hasAnyUnsavedClustersPlanningData = () => {
    return clusters.value.some((cluster) => cluster.hasChanges());
  };

  return {
    clusters,
    lessonGroups,
    weeks,
    deltaState,
    grid,
    hasGridGroups,
    isRendered,
    init,
    startDelta,
    endDelta,
    updateDelta,
    removeItemFromGrid,
    toggleGroupVisibility,
    findGridGroupIdByIndex,
    addGridItem,
    updateClusterPlanning,
    addCluster,
    findClusterById,
    removeCluster,
    setDraggingStateOnClusterGroup,
    hasAnyUnsavedClustersPlanningData,
  };
};

export const clusterManager = ref(createClusterManager());

export const useClusterManager = () => {
  const isInitialized = ref(false);

  onMounted(async () => {
    await clusterManager.value.init();

    isInitialized.value = true;
  });

  return { clusterManager, isInitialized };
};
